import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import battle1 from "../../assets/images/Battles/battle1.png";
import battle2 from "../../assets/images/Battles/battle2.png";
import "./battle.css";
import { useSelector, useDispatch } from "react-redux";
import { routes } from "../../routes";
import { useNavigate } from "react-router";
import { toggleOnboard } from "../../redux/slices/onBoardingSlice";
import { joinBattle, enterBattle } from "../../api/modules/b2droppie.class";
import { ReactComponent as Swords } from "../../assets/icons/swords.svg";
import { battleWSManager } from "../../services/battleWebSocketManager";
import { Eye } from "lucide-react";
import { toast } from "react-toastify";
import {ReactComponent as CrazyZap} from "../../assets/icons/crazy_zap.svg";

const Battle = ({ item }) => {
  const isLoggedIn = useSelector((state) => state?.auth?.isLoggedIn);
  const user = useSelector((state) => state?.auth?.user);
  const balance = useSelector((state) => state?.account?.balance);
  const dispatch = useDispatch();
  const [topUp, setTopup] = useState(false);
  const [currentBoxIndex, setCurrentBoxIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const isDarkMode = useSelector((state) => state?.theme?.darkMode);
  const scrollRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 769);
  const [hasEnteredBattle, setHasEnteredBattle] = useState(false);
  
  // ✅ Local state for live battle data
  const [liveBattleData, setLiveBattleData] = useState({
    status: item?.status,
    current_box: item?.current_box ?? 0,
    animating_box: item?.animating_box ?? 0,
    players: item?.players || [],
  });

  // ✅ Queue for tick events to process with delay
  const [tickQueue, setTickQueue] = useState([]);
  const processingTickRef = useRef(false);

  // ✅ Get last event from Redux
  const lastEvent = useSelector((state) => state?.battle?.lastEvent);

  // ✅ State to persist player avatars
  const [playerAvatars, setPlayerAvatars] = useState({
    player1: null,
    player2: null
  });

  // Memoize the entire item to prevent parent rerenders
  const memoizedItem = useMemo(
    () => item,
    [item?.id, item?.status, JSON.stringify(item?.boxes), JSON.stringify(item?.players)]
  );

  // ✅ Helper function to safely extract avatar
  const getPlayerAvatar = useCallback((player, fallback) => {
    if (!player) return fallback;
    
    // Check all possible avatar paths
    return (
      player?.user?.userdetails?.avatar ||
      player?.userdetails?.avatar ||
      player?.avatar ||
      fallback
    );
  }, []);


  // ✅ Subscribe to WebSocket for this battle
  useEffect(() => {
    // ✅ Subscribe to ALL battles
    if (item?.uuid) {
      battleWSManager.subscribe(item.uuid);
    }

    return () => {
      if (item?.uuid) {
        battleWSManager.unsubscribe(item.uuid);
      }
    };
  }, [item?.uuid, item?.id]);

  // ✅ Listen for events related to THIS battle
  useEffect(() => {
    if (!lastEvent) return;

    // Check if this event is for this battle
    const isForThisBattle = 
      lastEvent._channel === `battle.${item?.uuid}` ||
      lastEvent.battle_uuid === item?.uuid;

    if (!isForThisBattle) return;


    switch (lastEvent.type) {
      case 'second_player_joined':
        setLiveBattleData(prev => {
          const playerExists = prev.players?.some(
            p => p.user_id === lastEvent.player?.id || p.id === lastEvent.player?.id
          );
          if (playerExists) return prev;

          return {
            ...prev,
            players: [
              ...prev.players,
              {
                user_id: lastEvent.player?.id,
                user: {
                  id: lastEvent.player?.id,
                  username: lastEvent.player?.username,
                  userdetails: { avatar: lastEvent.player?.avatar }
                }
              }
            ],
          };
        });
        break;

      case 'started':
        setLiveBattleData(prev => ({
          ...prev,
          status: 'running',
          current_box: 0,
          animating_box: 0,
        }));
        break;

      case 'tick':
        // ✅ Add tick event to queue with preserved avatar data
        setTickQueue(prev => [...prev, {
          box_index: lastEvent.box_index,
          players: lastEvent.players?.map(p => ({
            ...p,
            user_id: p.user_id || p.player_id,
            player_id: p.player_id,
            total_unboxed_value: p.total_unboxed_value,
            user: {
              id: p.user_id || p.player_id,
              username: p.username,
              userdetails: {
                avatar: p.avatar || p.user?.userdetails?.avatar
              }
            }
          })),
          timestamp: Date.now()
        }]);
        break;

      case 'ended':
      case 'battle.finished':
        setLiveBattleData(prev => ({
          ...prev,
          status: 'finished',
        }));
        break;

      case 'cancelled':
      case 'battle.cancelled':
        setLiveBattleData(prev => ({
          ...prev,
          status: 'cancelled',
        }));
        break;

      default:
        if (lastEvent.box_index !== undefined) {
          setTickQueue(prev => [...prev, {
            box_index: lastEvent.box_index,
            timestamp: Date.now()
          }]);
        }
        break;
    }
  }, [lastEvent, item?.id, item?.uuid]);

  // ✅ Process tick queue with 10-second delay (matching BattleDetail animation)
  useEffect(() => {
    if (tickQueue.length === 0 || processingTickRef.current) {
      return;
    }

    const processNextTick = async () => {
      processingTickRef.current = true;
      const tickEvent = tickQueue[0];

      // ✅ Update to show animation is starting
      setLiveBattleData(prev => ({
        ...prev,
        status: 'running',
        animating_box: tickEvent.box_index,
        players: tickEvent.players || prev.players,
      }));

      // ✅ Wait 10 seconds to match BattleDetail's animation timing
      await new Promise(resolve => setTimeout(resolve, tickEvent.box_index === 0 ? 14000 : 10000));

      // ✅ After animation completes, update current_box
      setLiveBattleData(prev => ({
        ...prev,
        current_box: tickEvent.box_index,
      }));


      // ✅ Remove processed tick from queue
      setTickQueue(prev => prev.slice(1));
      processingTickRef.current = false;
    };

    processNextTick();
  }, [tickQueue]);

  // ✅ Calculate display box index
  const displayBoxIndex = useMemo(() => {
    const { animating_box, current_box } = liveBattleData;
    
    if (animating_box !== undefined && animating_box !== null && animating_box >= 0) {
      return animating_box;
    }
    if (current_box !== undefined && current_box !== null && current_box >= 0) {
      return current_box;
    }
    return 0;
  }, [liveBattleData.animating_box, liveBattleData.current_box]);

  useEffect(() => {
    const battleId = memoizedItem?.id;
    const hasEntered = localStorage.getItem(`battle_entered_${battleId}`);
    if (hasEntered === 'true') {
      setHasEnteredBattle(true);
    }
  }, [memoizedItem?.id]);

  const encryptData = useCallback((data) => {
    return window.btoa(data.toString());
  }, []);

  const handleJoinBattleAsPlayer2 = useCallback(() => {
    const encryptedData = encryptData(memoizedItem?.id);
    const decryptedData = memoizedItem?.id;

    if (!isLoggedIn) {
      dispatch(toggleOnboard({ login: true }));
      return;
    }

    if (balance < parseFloat(memoizedItem?.stake_amount)) {
       toast.warning("Insufficient balance", {
        autoClose: 3000,
        position: "top-center",
      });
      setTopup(true);
      return;
    }

    setIsLoading(true);

    joinBattle(decryptedData)
      .then((response) => {

        const isSuccess =
          response?.data?.success === true ||
          response?.success === true ||
          response?.data?.message === "Joined" ||
          response?.message === "Joined";

        if (isSuccess) {
          localStorage.setItem(
            `battle_waiting_${decryptedData}`,
            JSON.stringify({
              waiting: true,
              battleId: decryptedData,
              timestamp: Date.now(),
            })
          );

          setTimeout(() => {
            navigate(`${routes.BATTLEDETAIL}/${encryptedData}`);
          }, 100);
        } else {
          console.warn("⚠️ Unexpected response format:", response);
          navigate(`${routes.BATTLEDETAIL}/${encryptedData}`);
        }
      })
      .catch((e) => {
        console.error("❌ Error joining battle:", e);
        console.error("Error details:", e.response?.data);

        if (e.response?.data?.message) {
          alert(e.response.data.message);
        } else {
          alert("Failed to join battle. Please try again.");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [memoizedItem?.id, memoizedItem?.stake_amount, isLoggedIn, balance, dispatch, navigate, encryptData]);

  const handleJoinBattle = useCallback(
    async (status, player) => {
      const encryptedData = encryptData(memoizedItem?.id);
      const battleId = memoizedItem?.id;
      
      if (
        status === 'running' && 
        !hasEnteredBattle && 
        user?.id === item.creator_id
      ) {
        try {
          await enterBattle(battleId);
          localStorage.setItem(`battle_entered_${battleId}`, 'true');
          setHasEnteredBattle(true);
        } catch (error) {
          console.error('❌ Enter battle failed:', error);
        }
      }
      
      navigate(`${routes.BATTLEDETAIL}/${encryptedData}`);
    },
    [memoizedItem?.id, navigate, encryptData, hasEnteredBattle, user?.id, item.creator_id]
  );

  const getBoxIcon = useCallback((rarity) => {
    switch (rarity?.toLowerCase()) {
      case "common": return "📦";
      case "rare": return "🎮";
      case "epic": return "⚡";
      case "legendary": return "👑";
      default: return "📦";
    }
  }, []);
const handleScroll = useCallback(() => {
  if (scrollRef.current && liveBattleData.status !== 'running') {
    const container = scrollRef.current;
    const scrollLeft = container.scrollLeft;
    const children = Array.from(container.children);
    let closestIndex = 0;
    let closestDistance = Infinity;

    // ← snap point: 12px from left (matches your padding: 0 12px)
    const snapPoint = scrollLeft + 12;

    children.forEach((child, index) => {
      const childLeft = child.offsetLeft;   // ← absolute position, not relative
      const distance = Math.abs(childLeft - snapPoint);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setCurrentBoxIndex(closestIndex);
  }
}, [liveBattleData.status]);

  // ✅ Memoize boxes array
  const boxes = useMemo(() => {
    if (!memoizedItem?.boxes) return [];

    return memoizedItem.boxes.map((battleBox) => {
      const boxImage =
        battleBox?.box?.box_images?.image ||
        battleBox?.box?.image ||
        null;

      return {
        name: battleBox?.box?.name || "Unknown Box",
        rarity: battleBox?.box?.rarity || "Common",
        category: battleBox?.box?.category || "Mystery",
        box_price: battleBox?.box?.box_price || 0,
        box_image: boxImage,
        id: battleBox?.box?.id,
        position: battleBox?.position,
      };
    });
  }, [memoizedItem?.boxes]);

  // ✅ Use live players data
  const players = useMemo(() => {
    return liveBattleData.players?.length > 0 
      ? liveBattleData.players 
      : (memoizedItem?.players || []);
  }, [liveBattleData.players, memoizedItem?.players]);

  // ✅ Update avatars when players change (but don't overwrite if already set)
  useEffect(() => {
    if (players && players.length > 0) {
      setPlayerAvatars(prev => ({
        player1: prev.player1 || getPlayerAvatar(players[0], battle1),
        player2: players[1] ? (prev.player2 || getPlayerAvatar(players[1], battle2)) : prev.player2
      }));
    }
  }, [players, getPlayerAvatar]);

  // ✅ Auto-scroll to current box with smooth behavior
 useEffect(() => {
  if (
    scrollRef.current && 
    boxes.length > 0 && 
    liveBattleData.status === 'running'
  ) {
    const container = scrollRef.current;
    const target = container.children[displayBoxIndex];

    if (target) {
      const targetScrollLeft = target.offsetLeft - 12;

      container.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth"
      });

      setCurrentBoxIndex(displayBoxIndex);
    }
  }
}, [displayBoxIndex, liveBattleData.status, boxes.length]);

  // ✅ Prevent manual scrolling during running battles
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const preventScroll = (e) => {
      if (liveBattleData.status === 'running') {
        e.preventDefault();
      }
    };

    el.addEventListener("wheel", preventScroll, { passive: false });
    el.addEventListener("touchmove", preventScroll, { passive: false });

    return () => {
      el.removeEventListener("wheel", preventScroll);
      el.removeEventListener("touchmove", preventScroll);
    };
  }, [liveBattleData.status]);

  const handleButtonClick = useCallback(() => {
    if (!user) {
      dispatch(toggleOnboard({ login: true }));
      return;
    }

    const currentStatus = liveBattleData.status || item.status;

    if (currentStatus === "finished") {
      handleJoinBattle(memoizedItem?.status, players?.filter((x) => x?.user_id == user?.id));
      return;
    }

    if (user.id === item.creator_id) {
      handleJoinBattle(memoizedItem?.status, players?.filter((x) => x?.user_id == user?.id));
      return;
    }

    const isUserInBattle = players?.some((record) => record.user_id == user?.id);
    if (isUserInBattle) {
      handleJoinBattle(memoizedItem?.status, players?.filter((x) => x?.user_id == user?.id));
      return;
    }

    if (players?.length <= 1) {
      handleJoinBattleAsPlayer2();
      return;
    }

    handleJoinBattle(memoizedItem?.status, players?.filter((x) => x?.user_id == user?.id));
  }, [user, item.status, item.creator_id, players, memoizedItem?.status, handleJoinBattle, handleJoinBattleAsPlayer2, dispatch, liveBattleData.status]);

  const getButtonText = useCallback(() => {
  const status = liveBattleData.status || item.status;

  if (status === "finished") return "Battle Results";

  if (!user && status === "running") return "Login to View";

  if (!user) return "Login to Join";

  if (user.id === item.creator_id) return "Go to Battle";

  if (status === "running") return "View Battle";

  const isUserInBattle = players?.some(
    (record) => record.user_id == user?.id
  );

  if (isUserInBattle) return "Go to Battle";

  if (status === "started") return "View Battle";

  return "Join Battle";
}, [liveBattleData.status, item.status, user, item.creator_id, players]);


const statusBadge = (status) => {
  const normalized = status?.toString()?.trim()?.toLowerCase();

  let displayText = normalized;

  if (normalized === "running" || normalized === "started") {
    displayText = "In Progress";
  } else if (normalized === "waiting") {
    displayText = "Awaiting Player";
  }

  return (
    <span className={`text-AktivGrotesk-Regular custom-status-badge ${normalized} `}>
      {displayText}
    </span>
  );
};
  const currentStatus = liveBattleData.status || item?.status;
  const isRunning = currentStatus === 'running';

  return (
    <div
      className={`${isDarkMode ? "battles-main battles-main-dark" : "battles-main battles-main-light"} ${isRunning ? "battle-running" : ""}`}
    >
      <div className="battle_header">
        <div className="col">
          {/* Price Box */}
          <div className="price_box">
            <div className="battle_price ">
              Battle Price ${parseFloat(item.stake_amount)}
              <span
                style={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
                className="pt-2"
              >
                {item?.private && (
                  <div className="mode-badge mode-badge--private">Private</div>
                )}
                {item?.mode === "crazy" && (
                  <div className="mode-badge mode-badge--crazy">Crazy Mode</div>
                )}
                {!item?.private && item?.mode !== "crazy" && (
                  <div className="mode-badge mode-badge--normal">Normal</div>
                )}
              </span>
            </div>
          </div>
          {/* Status Badge */}
          <div>
            <div className="battle-status pt-2">
              {currentStatus
                ? statusBadge(currentStatus.toLowerCase())
                : "Waiting"}
            </div>
          </div>
        </div>
        <div className="col">
          {/* Footer Button */}
          <div style={{ display: "flex", justifyContent: "end" }}>
            <button
              onClick={handleButtonClick}
              className="battle-btn"
              disabled={isLoading}
              style={{
                opacity: isLoading ? 0.6 : 1,
                cursor: isLoading ? "not-allowed" : "pointer",
                color: getButtonText() === "Join Battle" ? "#FFF" : "#0D0D0D",
                backgroundColor:
                  getButtonText() === "Join Battle"
                    ? "#1E4BFF"
                    : getButtonText() === "View Battle"
                      ? "#333"
                      : "#FFD342",
                borderTop: `1px solid ${
                  getButtonText() === "Join Battle"
                    ? "#6281FF"
                    : getButtonText() === "View Battle"
                      ? "#333"
                      : "#FFD342"
                }`,
              }}
            >
              {getButtonText() === "View Battle" && (
                <span className="eye-icon">
                  <Eye />
                </span>
              )}
              {isLoading ? "Joining..." : getButtonText()}
            </button>
          </div>
        </div>
      </div>
      <div className="battles-head-icons">
        {/* Player 1 */}
        <div className="player-info player-left">
          <img
            src={playerAvatars.player1 || battle1}
            alt="Player 1"
            className="battles-head-img"
            onError={(e) => {
              e.currentTarget.src = battle1;
            }}
          />
        </div>

        {/* VS Icon */}
        <div className="vs-container">
        { item?.mode === "crazy" ? <CrazyZap style={{height:"24px",width:"24px"}}/>:  <Swords color="#FFCB21" size={19} />}
        </div>

        {/* Player 2 */}
        <div className="player-info player-right">
          {players?.[1] ? (
            <img
              src={playerAvatars.player2 || battle2}
              alt="Player 2"
              className="battles-head-img"
              onError={(e) => {
                e.currentTarget.src = battle2;
              }}
            />
          ) : (
            <div className="dummy-image"></div>
          )}
        </div>
      </div>

      {/* Battle Boxes Section */}
      <div className="battle-boxes">
        <div
          className={`battle_box_list ${isDarkMode ? "dark_bg" : "light_bg"}`}
        >
           <div className="box-counter text-AktivGrotesk-Regular">
    {currentBoxIndex + 1}/{boxes.length}
  </div>

          <div className="boxes-list" ref={scrollRef} onScroll={handleScroll}>
            {boxes.map((box, i) => {
              return (
                <div
                  key={box.id || i}
                  className={`battle-box ${i === currentBoxIndex ? "active" : "inactive"}`}
                >
                  <div className="box-left">
                    <div className="box-icon">
                      {box.box_image ? (
                        <img
                          style={{
                            width: "77px",
                            height: "63px",
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                          src={box.box_image}
                          onError={(e) => {
                            const parent = e.currentTarget.parentElement;
                            e.currentTarget.style.display = "none";
                            parent.style.fontSize = "44px"; // icon size

                            parent.innerHTML = getBoxIcon(box?.rarity);
                          }}
                          alt={box?.name}
                        />
                      ) : (
                        <span style={{ fontSize: "40px" }}>
                          {getBoxIcon(box?.rarity)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Battle;